import { useEffectOnValueChange, useThrottledCallback } from '@timelyapp/react';
import classNames from 'classnames';
import { FALLBACK_COLOR_NEUTRAL } from 'constants/colors';
import { useDragHandle } from 'hooks/useDragHandle';
import { clamp } from 'lodash-es';
import React, { useCallback, useMemo } from 'react';
import { isContrastReadable, prefixHexColor } from 'utils/colors';
import { fromISO } from 'utils/luxon-helpers';
import { pick } from 'utils/typesafe';
import styles from './GanttChart.module.scss';
import type {
  GanttLaneSpecification,
  GanttLayoutMeta,
  HeaderDatePreview,
} from './gantt-internal-types';
import { PROJECT_TASK_HEIGHT, USER_TASK_HEIGHT } from './gantt-layout';
import type { GanttRow } from './gantt-types';

interface Props {
  showMouseOverPreview: boolean;
  ganttRow: GanttRow;
  laneSpecification: GanttLaneSpecification;
  height: number;
  layoutMeta: GanttLayoutMeta;
  onCreateTask: (parameters: { startDate: string; endDate: string }) => void;
  onUpdateResizePreview: (dates: HeaderDatePreview | null) => void;
}

const GanttChartDragToCreateLayer: React.FC<Props> = props => {
  const {
    showMouseOverPreview,
    ganttRow,
    laneSpecification,
    height: rowHeight,
    layoutMeta,
    onUpdateResizePreview,
    onCreateTask,
  } = props;

  const snapToLane = useCallback(
    (offsetTop: number, extendToBottom = false) => {
      const { padTop, laneHeight, laneSpacing } = laneSpecification;
      const laneOffsetHeight = laneHeight + laneSpacing;

      // Step 1: find the current lane
      let top = padTop;
      while (offsetTop > top && top + laneHeight < rowHeight) {
        top += laneOffsetHeight;
      }
      // Compensate for the last loop iteration which causes an overshoot
      top -= laneOffsetHeight;
      // Step 2: Ensure top is within bounds
      top = clamp(top, padTop, rowHeight - laneHeight - laneSpacing);

      let height = laneHeight;
      // If we're on the last row, extend all the way to the bottom so that you can
      // drag even on a "full" row (the drag-to-create boxes will extend below tasks on last row)
      if (extendToBottom && top + laneHeight * 2 >= rowHeight) {
        height = rowHeight - top - laneSpacing;
      }
      return {
        top,
        height,
      };
    },
    [laneSpecification, rowHeight]
  );

  const processOffsets = useCallback(
    (delta: number, offset: number, offsetTop: number) => {
      let dayOffset = Math.floor(
        (offset + layoutMeta.zoom.dayPadding / 2) / layoutMeta.zoom.columnWidth
      );
      let endDayOffset = Math.floor(
        (offset + delta + layoutMeta.zoom.dayPadding / 2) / layoutMeta.zoom.columnWidth
      );
      if (dayOffset > endDayOffset) {
        [dayOffset, endDayOffset] = [endDayOffset, dayOffset];
      }
      const lengthInDays = Math.max(1, endDayOffset - dayOffset + 1);
      const startDate = fromISO(layoutMeta.startDate).plus({ days: dayOffset });
      const endDate = startDate.plus({ days: lengthInDays - 1 });
      const left = dayOffset * layoutMeta.zoom.columnWidth;
      const width = lengthInDays * layoutMeta.zoom.columnWidth - layoutMeta.zoom.dayPadding;

      return {
        startDate: startDate.toISODate(),
        endDate: endDate.toISODate(),
        left,
        width,
        ...snapToLane(offsetTop),
      };
    },
    [layoutMeta, snapToLane]
  );

  const handleDragDone = useCallback(
    (delta: number, initialOffset: { x: number; y: number }) => {
      const offsets = processOffsets(delta, initialOffset.x, initialOffset.y);
      onCreateTask(offsets);
    },
    [processOffsets, onCreateTask]
  );

  const [dragHandleRef, resizingDelta, initialMouseOffsetOnHandle] = useDragHandle(handleDragDone);

  let createTaskPreview: ReturnType<typeof processOffsets> | null = null;
  if (resizingDelta != null && initialMouseOffsetOnHandle != null) {
    createTaskPreview = processOffsets(
      resizingDelta,
      initialMouseOffsetOnHandle.x,
      initialMouseOffsetOnHandle.y
    );
  }

  useEffectOnValueChange(pick(createTaskPreview, 'startDate', 'endDate'), () => {
    onUpdateResizePreview(createTaskPreview);
  });

  const displayColor =
    ganttRow.type === 'user' ? FALLBACK_COLOR_NEUTRAL : prefixHexColor(ganttRow.displayColor);
  const contrastReadable = useMemo(() => isContrastReadable('#fff', displayColor), [displayColor]);

  interface MouseOverSpec {
    left: number;
    top: number;
    height: number;
  }
  const [mouseOverPreview, setMouseOverPreview] = React.useState<MouseOverSpec | null>(null);
  const handleUpdateMouseOverPreview = useThrottledCallback(
    (target: Element, coords: { clientX: number; clientY: number }) => {
      const { clientX, clientY } = coords;
      const { left: boxX, top: boxY } = target.getBoundingClientRect();
      const { top, height } = snapToLane(clientY - boxY, true);
      const left =
        Math.floor(
          (clientX - boxX + layoutMeta.zoom.dayPadding / 2) / layoutMeta.zoom.columnWidth
        ) * layoutMeta.zoom.columnWidth;
      setMouseOverPreview({ top, height, left });
    },
    1000 / 30
  );
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const { clientX, clientY } = e;
      handleUpdateMouseOverPreview(e.currentTarget, { clientX, clientY });
    },
    [handleUpdateMouseOverPreview]
  );
  const handleMouseLeave = useCallback(() => {
    setMouseOverPreview(null);
    handleUpdateMouseOverPreview.cancel();
  }, [setMouseOverPreview, handleUpdateMouseOverPreview]);

  return (
    <div
      ref={dragHandleRef}
      className={classNames(styles.dragToCreateLayer, {
        [styles.creating]: createTaskPreview != null,
      })}
      onMouseMove={showMouseOverPreview ? handleMouseMove : undefined}
      onMouseLeave={handleMouseLeave}
    >
      {showMouseOverPreview && createTaskPreview == null && mouseOverPreview && (
        <div
          className={styles.mouseOverPreview}
          style={{ ...mouseOverPreview, width: layoutMeta.zoom.dayWidth }}
        />
      )}
      {createTaskPreview != null && (
        <div
          className={classNames(styles.task, styles.resizing, {
            [styles.darkText]: !contrastReadable,
          })}
          style={{
            top: createTaskPreview.top,
            left: createTaskPreview.left,
            width: createTaskPreview.width,
            height: ganttRow.type === 'user' ? USER_TASK_HEIGHT : PROJECT_TASK_HEIGHT,
            background: displayColor,
          }}
        >
          <div className={styles.taskContent}>
            <div className={styles.text}>
              <div className={styles.title}>New task</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default GanttChartDragToCreateLayer;
